import { appConfigStore } from 'Models/AppConfigStore';
import React from 'react';
import moment from 'moment/moment';

import CalendarDisabledSvg from 'images/calender-disabled.svg';
import CalendarSvg from 'images/calender.svg';
import { store } from 'Models/Store';
import { TripDetailsModel } from 'Modules/TripDetails/TripDetails';
import { getMessageByKey } from 'Stores/LanguageStore';
import { DatePicker } from 'Views/Components/DatePicker/DatePicker';
import If from 'Views/Components/If/If';
import { DisplayType } from 'Views/Components/Models/Enums';

export interface DateRangePickerProps {
	model: TripDetailsModel;
	onDatesChanged: (startDate: Date, departureDate: boolean, updateOtherDate: boolean) => void;
	changeBothDates: boolean;
	departureTrip: boolean | undefined;
	className?: string;
	disabled?: boolean;
}

export function DateRangePicker({
	model,
	onDatesChanged,
	changeBothDates,
	departureTrip,
	className,
	disabled = false,
}: DateRangePickerProps) {
	return (
		<div className="date-range-picker__container">
			<DatePicker
				model={model}
				modelProperty="startDate"
				mode="single"
				className="date-picker__general trip-details__date-picker"
				displayType={DisplayType.BLOCK}
				minDate="today"
				flatpickrOptions={{
					position: 'auto',
					minDate: store.isStaff ? '' : 'today',
					monthSelectorType: 'static',
					allowInput: false,
					locale: {
						firstDayOfWeek: appConfigStore.CalendarStartDay,
						weekdays: {
							shorthand: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
							longhand: ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'],
						},
					},
					disableMobile: true,
				}}
				flatpickrProps={{
					value: model.startDate,
					onChange: (dates, currentDateString, self, data) => {
						const [date] = dates;
						const newDate = new Date(date);
						const endDate = new Date(model.startDate);

						onDatesChanged(date, true, endDate < newDate && changeBothDates);
					},
					render: (props, ref) => {
						return (
							<button
								className="unstyled-button trip-details-calendar-row"
								ref={ref as any}
								disabled={departureTrip === false || disabled}
							>
								<CustomDateRangeFilter
									model={model}
									disabled={departureTrip === false || disabled}
									departureDate
								/>
							</button>
						);
					},
				}}
			/>
			<If condition={model.wizardData.tabSelected !== 'freight'}>
				<DatePicker
					model={model}
					modelProperty="endDate"
					mode="single"
					className="date-picker__general trip-details__date-picker"
					displayType={DisplayType.BLOCK}
					minDate="today"
					flatpickrOptions={{
						position: 'auto',
						minDate: store.isStaff ? '' : 'today',
						monthSelectorType: 'static',
						allowInput: false,
						locale: {
							firstDayOfWeek: appConfigStore.CalendarStartDay,
							weekdays: {
								shorthand: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
								longhand: ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'],
							},
						},
						disableMobile: true,
					}}
					flatpickrProps={{
						value: model.endDate,
						onChange: (dates, currentDateString, self, data) => {
							const [date] = dates;
							const newDate = new Date(date);
							const startDate = new Date(model.startDate);

							onDatesChanged(date, false, newDate < startDate && changeBothDates);
						},
						render: (props, ref) => {
							return (
								<button
									className="unstyled-button trip-details-calendar-row"
									ref={ref as any}
									disabled={
										departureTrip === true ||
										(departureTrip === undefined && model.tripTypeTabSelected === 'one way') ||
										disabled
									}
								>
									<CustomDateRangeFilter
										model={model}
										disabled={
											departureTrip === true ||
											(departureTrip === undefined && model.tripTypeTabSelected === 'one way') ||
											disabled
										}
										departureDate={false}
									/>
								</button>
							);
						},
					}}
				/>
			</If>
		</div>
	);
}

interface ICustomDateRangeFilterProps {
	model: TripDetailsModel;
	disabled: boolean;
	departureDate: boolean;
}

function CustomDateRangeFilter({ model, disabled, departureDate }: ICustomDateRangeFilterProps) {
	const classPrefix = departureDate ? 'departure' : 'return';

	return (
		<div className="custom-date-selector__container show">
			<p className="date-range-input__label">
				{departureDate
					? getMessageByKey('depart-date-input__label', 'Depart')
					: getMessageByKey('return-date-input__label', 'Return')}
			</p>

			<div className="date-range-input__input">
				<img
					className="date-calendar-icon"
					src={disabled ? CalendarDisabledSvg : CalendarSvg}
					alt={`${classPrefix}-calendar-icon`}
				/>
				<p className={`date__label ${disabled ? 'disabled' : ''}`}>
					{moment(departureDate ? model.startDate : model.endDate).format('DD MMM YYYY')}
				</p>
			</div>
		</div>
	);
}
